import { t } from '../i18n';
import { useEffect, useState, useCallback } from 'react';
import { AppState } from 'react-native';
import { collection, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { firestoreCache, CACHE_KEYS, CACHE_TTL } from '../utils/firestoreCache';
import { guardedGetDoc, guardedGetDocs } from '../utils/guardedFirestore';
import { getGradeSubmissionKey } from '../utils/courseIdentity';
import { getCurrentSemester, isInGradePeriod } from '../constants/reviewPeriods';
import { GateStatus } from '../types/gate';

const REQUIRED_GRADE_COUNT = 2;

type UseGradeGateResult = {
  isGated: boolean;
  submittedCount: number;
  requiredCount: number;
  loading: boolean;
  status: GateStatus;
  error: string | null;
  retry: () => Promise<void>;
  setExempt: () => Promise<void>;
  notifyGradeSubmitted: () => void;
  currentSemester: { academicYear: string; semester: string };
};

export const useGradeGate = (): UseGradeGateResult => {
  const [isGated, setIsGated] = useState(false);
  const [submittedCount, setSubmittedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<GateStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [currentSemester, setCurrentSemester] = useState({ academicYear: '', semester: '' });

  const markUnavailable = useCallback((cause: unknown) => {
    console.error('[useGradeGate] Failed to check gate status:', cause);
    setIsGated(true);
    setError(t("成績入力状況を確認できませんでした"));
    setStatus('unavailable');
  }, []);

  const checkGateStatus = useCallback(async (userUid: string, ignoreCache = false) => {
      setLoading(true);
      setError(null);
      // ローカル環境（テスト環境）では一時的に成績ゲートをオフにする
      // if (__DEV__) {
      //   setIsGated(false);
      //   setLoading(false);
      //   return;
      // }

      if (!isInGradePeriod()) {
        setIsGated(false);
        setStatus('ready');
        setLoading(false);
        return;
      }

      const semester = getCurrentSemester();
      setCurrentSemester(semester);
      setIsGated(true);
      setStatus('loading');

      const cacheKey = CACHE_KEYS.gradeGate(userUid, semester.academicYear, semester.semester);
      const cached = ignoreCache
        ? null
        : firestoreCache.get<{ isGated: boolean; submittedCount: number }>(cacheKey);
      if (cached) {
        setIsGated(cached.isGated);
        setSubmittedCount(cached.submittedCount);
        setStatus('ready');
        setLoading(false);
        return;
      }

      try {
        const userDoc = await guardedGetDoc(doc(db, 'users', userUid));
        if (!userDoc) {
          throw new Error('grade gate user read skipped by quota guard or circuit breaker');
        }

        const userData = userDoc.exists() ? userDoc.data() : null;
        const exemptData = userData?.gradeExempt;
        const currentSemesterKey = `${semester.academicYear}${semester.semester}`;

        if (exemptData?.semester === currentSemesterKey) {
          setIsGated(false);
          setStatus('ready');
          return;
        }

        const markerSnapshot = await guardedGetDocs(collection(db, 'users', userUid, 'gradeSubmissions'));
        if (!markerSnapshot) {
          throw new Error('grade gate marker read skipped by quota guard or circuit breaker');
        }

        const count = markerSnapshot.docs.reduce((total, marker) => {
          const markerData = marker.data() as {
            courseId?: string;
            academicYear?: string;
            semester?: string;
          };
          if (
            markerData.academicYear !== semester.academicYear ||
            markerData.semester !== semester.semester ||
            !markerData.courseId
          ) {
            return total;
          }

          try {
            const expectedKey = getGradeSubmissionKey(
              { id: markerData.courseId, academicYear: markerData.academicYear },
              markerData.semester
            );
            return total + (marker.id === expectedKey ? 1 : 0);
          } catch {
            return total;
          }
        }, 0);
        const gated = count < REQUIRED_GRADE_COUNT;

        setSubmittedCount(count);
        setIsGated(gated);
        setStatus('ready');

        firestoreCache.set(cacheKey, { isGated: gated, submittedCount: count }, CACHE_TTL.GRADE_GATE);
      } catch (error) {
        markUnavailable(error);
      } finally {
        setLoading(false);
      }
  }, [markUnavailable]);

  const retry = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      setIsGated(false);
      setStatus('ready');
      setError(null);
      setLoading(false);
      return;
    }

    const semester = getCurrentSemester();
    firestoreCache.invalidate(CACHE_KEYS.gradeGate(user.uid, semester.academicYear, semester.semester));
    await checkGateStatus(user.uid, true);
  }, [checkGateStatus]);

  useEffect(() => {

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        void checkGateStatus(user.uid);
      } else {
        setIsGated(false);
        setStatus('ready');
        setError(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [checkGateStatus]);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'active' && auth.currentUser) {
        void retry();
      }
    });
    return () => subscription.remove();
  }, [retry]);

  const setExempt = async () => {
    if (!auth.currentUser) return;

    const semester = getCurrentSemester();
    const semesterKey = `${semester.academicYear}${semester.semester}`;

    await updateDoc(doc(db, 'users', auth.currentUser.uid), {
      gradeExempt: {
        semester: semesterKey,
        grantedAt: serverTimestamp(),
      },
    });

    firestoreCache.invalidate(
      CACHE_KEYS.gradeGate(auth.currentUser.uid, semester.academicYear, semester.semester)
    );

    setIsGated(false);
    setStatus('ready');
    setError(null);
  };

  // 新規成績報告成功時に呼び出し、Firestore再取得を待たずに件数とゲート状態を即時反映する
  const notifyGradeSubmitted = useCallback(() => {
    if (!auth.currentUser) return;
    if (status !== 'ready') {
      void retry();
      return;
    }

    setSubmittedCount((prevCount) => {
      const nextCount = prevCount + 1;
      const nextGated = nextCount < REQUIRED_GRADE_COUNT;
      setIsGated(nextGated);

      const cacheKey = CACHE_KEYS.gradeGate(
        auth.currentUser!.uid,
        currentSemester.academicYear,
        currentSemester.semester
      );
      firestoreCache.set(
        cacheKey,
        { isGated: nextGated, submittedCount: nextCount },
        CACHE_TTL.GRADE_GATE
      );

      return nextCount;
    });
  }, [currentSemester, retry, status]);

  return {
    isGated,
    submittedCount,
    requiredCount: REQUIRED_GRADE_COUNT,
    loading,
    status,
    error,
    retry,
    setExempt,
    notifyGradeSubmitted,
    currentSemester,
  };
};
